// @flow

import React from 'react';
import ReactDOM from 'react-dom';
import { defer } from 'lodash';

import type { Node } from 'react';

type Props = {
  /** Whether to listen for outside click events. */
  isListening: boolean,
  /** Handler for outside click event. */
  onClickOutside: () => void,
  children: Node,
};

/**
 * A component to alert when the user has clicked outside of its children.
 */
export default class OutsideClickable extends React.Component<Props> {
  static defaultProps = {
    isListening: true,
  };

  _isMounted: boolean = false;

  componentDidMount() {
    // `defer` binding clicks because it's possible that a click
    // resulted in rendering/mounting this component, and that
    // the click is still ongoing. By definition, the click must
    // have occurred outside of this component since it didn't
    // exist yet, and since it occurred outside, our `onClick`
    // method may invoke immediately (since that's the whole point
    // of this component--outside clicks) which could result in this
    // component being immediately torn down.
    if (this.props.isListening) {
      defer(() => this.bindClicks(true));
    }
    this._isMounted = true;
  }

  componentWillUnmount() {
    this.bindClicks(false);
    this._isMounted = false;
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.isListening !== this.props.isListening) {
      this.bindClicks(this.props.isListening);
    }
  }

  bindClicks(shouldBind: boolean) {
    // Note that this is bound to `html` so that `onClick`
    // fires *before* React's global click handler which is
    // attached to `document`, via event propagation. Without
    // this, the `click` event's callbacks would fire which
    // may end up synchronously rendering this `OutsideClickable`
    // component and thereby binding this very function *during*
    // the click that originated its rendering. And by the time
    // the event bubbled up to our handler (if it was on `document`
    // or `window`), we'd enter `this.onClick` which would fail
    // because `ownNode` would never equal `e.target` if `e.target`
    // was removed from the DOM because it was replaced by this node.
    const htmlDocument = document.querySelector('html');

    if (!htmlDocument) return this;

    const method = shouldBind ? 'addEventListener' : 'removeEventListener';

    // $FlowFixMe
    htmlDocument[method]('click', this.onClick);

    return this;
  }

  onClick = (e: MouseEvent) => {
    // Wait until the call stack is cleared before calling
    // `onClickOutside`. This is important because if we
    // call it immediately, the event will still be bubbling
    // and not all handlers may have been called. For instance,
    // you could click outside of this node on a button within
    // a popover, and if `onClickOutside` is called immediately
    // and it changes state which closes that popover, by the
    // time the event reaches `document`, the popover and button
    // will have been unmounted and their callback wouldn't be
    // called. So in this case we want the click handler of the
    // button within the popover to be called to do whatever it
    // needs to do, and then via `defer` we'll call `onClickOutside`
    // which *could* unmount the popover. This is all just a (real)
    // example but there are many use cases for this.
    if (this._isMounted) {
      const ownNode = ReactDOM.findDOMNode(this);

      if (!ownNode) return;

      const didClickWithin = ownNode.contains((e.target: any));
      defer(() => {
        if (!didClickWithin) {
          this.props.onClickOutside();
        }
      });
    }
  };

  render() {
    return this.props.children;
  }
}
